import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Public landing page for the corporate nutrition webinar (no login): the link Anish shares with a
// company's HR, who forward it to their employees. First one: GAC Dubai, Thursday 5 Nov 2026, 11:00 Dubai
// time, on Zoom, 25 minutes, hosted by Anish. Says what we'll cover, why it matters at a desk job, what
// people believe today vs what they'll leave knowing, the three live activities, real LIFTR Dubai reviews
// about food, and a countdown. Not salesy (Anish, 30 Oct): no booking button, one quiet link to the Dubai page.
//
// Looks like a printed invitation, not a SaaS page (Anish, 30 Oct: "not so AI"): warm paper, a serif
// display face, hairline rules instead of cards, the countdown as a line of text.
//
// Query params (all optional):   ?for=Acme           personalises the page for one company
//                                ?join=<zoom url>    shows a "Join on Zoom" button (HR usually shares it themselves)
//                                ?at=2026-11-12T11:00+04:00   another date/time (the next company)
// The talking points come from our own nutrition course (backend/routes/nutrition_course.py), the one
// 6WC members and Dubai trial members do in the app.

const DUBAI = "https://www.liftr.fit/locations/dubai";
const MAPS = "https://maps.app.goo.gl/LTjSxaS7Hp1T2iQd7";
// The Dubai trial week button comes back once the trial is live (Anish, 30 Oct): TRIAL = "https://app.liftr.info/try/dubai".
const EMAIL = "anish@liftr.fit";
const DEFAULT_START = "2026-11-05T11:00:00+04:00";
// LIFTR hosts the Zoom (Anish, 30 Oct). The GAC session's link; ?join= overrides it for the next company.
const DEFAULT_JOIN = "https://us06web.zoom.us/j/83628403346?pwd=leWS7pKVx7KHfdAJcnSsX83lfEJ6vz.1";
const TZ = "Asia/Dubai";
const MINUTES = 25;
const IMG = {
  anish: "/brand/anish.jpg",
  smoothie: "/webinar/healthy5.jpg",
  sugar: "/webinar/sugar.jpg",
  cola: "/webinar/cola.jpg",
  plate: "/webinar/plate1.jpg",
  group: "https://www.liftr.fit/img/blog/batch-photo.jpg",
  squat: "https://www.liftr.fit/img/gym/hero-coached-squat.jpg",
};
// Photo credits for the Commons pictures whose licence asks for one.
const CREDITS = "Photos: Shixart1985 (CC BY 2.0), HaJunkiyada (CC BY-SA 4.0), via Wikimedia Commons";
const FONTS = "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,300;1,9..144,500&display=swap";

const AGENDA = [
  { min: 5, title: "The basics", text: "Energy in, energy out, and why a big-carb lunch plus a sweet drink is the afternoon slump. Why \"healthy\" is a trap." },
  { min: 5, title: "How to read a nutrition label", text: "Serving size first, then sugar and protein. Live, with a packet from your desk drawer." },
  { min: 9, title: "Marketing gimmicks and myths", text: "\"High protein\", \"no added sugar\", rice at night, detox teas. Zero-sugar drinks, caffeine, and which supplements are worth it." },
  { min: 6, title: "Build your own meal plan", text: "One of each on every plate. A plan you can make in two minutes, from food you already eat, that keeps you sharp till 6." },
];

const ACTIVITIES = [
  { title: "Rank the drinks", text: "Five everyday drinks. Put them in order by calories. Most people get the latte wrong." },
  { title: "Label from your drawer", text: "Grab any packet near you. We read it together: serving, sugar, protein, and the claim on the front." },
  { title: "Your lunch, rebuilt", text: "Take what you ate yesterday and make it a LIFTR plate: half veg, a palm of protein, a handful of carbs." },
];

// What people say in their first consult with us vs what the course teaches (nutrition_course.py).
const SHIFTS = [
  { before: "Carbs make you fat, so I skip rice.", after: "Eating more than you burn does. Rice, bread and potatoes are fine in the right portion." },
  { before: "I'll skip breakfast and burn it off with a walk.", after: "A 45-minute walk is about 200 kcal. One croissant is about 270. Food does the work; training keeps the muscle." },
  { before: "I drink juice and smoothies because they're healthy.", after: "Juice is the sugar without the fibre. Eat the fruit, don't drink it." },
  { before: "Protein powder is for bodybuilders.", after: "It's milk or plant protein, dried. A scoop is 20 to 25 g of protein for about 120 kcal." },
  { before: "Zero-sugar drinks must be bad for you.", after: "They don't stop fat loss. Swapping a sugary drink for one is one of the easiest wins there is." },
  { before: "I need a diet plan from an expert.", after: "You need one protein, one carb, vegetables and a little fat at every main meal. You can build that yourself." },
  { before: "I'm just not a 3 pm person.", after: "The slump is usually a big-carb lunch and a sweet drink. Change the plate, keep the afternoon." },
];

// A short true-or-false, from the course's quick checks. Tapping shows the answer: a taste of the webinar.
const QUIZ = [
  { q: "Honey and brown sugar are healthier than white sugar.", truth: false, why: "About the same calories, and your body treats them much the same." },
  { q: "A can of regular cola has about 140 kcal, all sugar.", truth: true, why: "Two a day is about 1 kg of fat a month if nothing else changes." },
  { q: "Creatine is a steroid.", truth: false, why: "It's the best-researched supplement there is, and your body already makes it." },
  { q: "You gained 1 kg overnight, so you gained fat.", truth: false, why: "Gaining 1 kg of fat takes about 7,700 extra calories. Overnight swings are water, salt and digestion." },
  { q: "Half the caffeine from a 3 pm coffee is still in you at 9 pm.", truth: true, why: "Stop about 8 hours before bed if sleep is a problem." },
];

const FAQ = [
  { q: "Do I need a camera on?", a: "No. Join from your desk, listen, and use the chat for the activities." },
  { q: "Is this a sales pitch?", a: "No. 25 minutes of nutrition you can use today, from the course our members do. The last two minutes are about how training and food fit together. Nothing to buy." },
  { q: "Will there be a recording?", a: "Yes, your HR team gets the recording and a one-page summary to share." },
  { q: "I'm vegetarian / diabetic / on a diet already. Is it for me?", a: "Yes. It works with whatever you normally eat, from wherever you're from, with vegetarian options throughout. For a medical condition, your doctor's advice comes first." },
];

// Real Google reviews of LIFTR Dubai (maps.app.goo.gl/LTjSxaS7Hp1T2iQd7, read 30 Oct 2026), the ones about food.
const REVIEWS = [
  { who: "Sreesanth P S", text: "My wife and I joined LIFTR just two weeks ago, and we're already seeing a big difference. I feel more energetic, my daily movements have improved, and I actually look forward to every workout." },
  { who: "Naresh Kapri", text: "I completed the 6-week program. I lost around 8 kg and noticed a good improvement in both my physical appearance and mental well-being." },
  { who: "Sangeetha Bhaskaran", text: "Losing weight is hard, but keeping that weight off is even harder. Liftr spent the first few weeks focusing on habit building, especially diet. This has helped me be mindful of my intake and I'm able to manage my cravings better." },
  { who: "Fiona Dsouza", text: "The food tracking also kept me accountable, and what initially felt like an obligation has slowly become a habit." },
  { who: "Karen Lobo", text: "They guide you with workouts and teach you how to eat right so fitness becomes part of your routine. Everyone starts from the same place, so there's no pressure or comparison." },
  { who: "Avinash Kale", text: "I lost 3 kgs and almost 5 inches so far, my strength is increasing everyday. Best part, I am in remission stage of my diabetes successfully. Well within 3 months' time." },
];

// Palette: warm paper, near-black ink, one red. Set inline so the app's dark theme doesn't leak in.
const INK = "#1b1813";
const PAPER = "#f3eee4";
const RULE = "#d9d2c3";
const MUTED = "#6b655a";
const RED = "#c41e3a";
const serif = { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif" };
const smallCaps = { letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 11, color: MUTED };

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = target - now;
  const s = Math.max(0, Math.floor(diff / 1000));
  return { diff, days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), mins: Math.floor((s % 3600) / 60), secs: s % 60 };
}

// The countdown as a sentence, set in the serif: "5 days, 22 hours and 14 minutes from now."
function Countdown({ start }) {
  const c = useCountdown(start.getTime());
  const live = c.diff <= 0 && c.diff > -MINUTES * 60 * 1000;
  const over = c.diff <= -MINUTES * 60 * 1000;
  let line;
  if (live) line = "Happening now. We're live on Zoom.";
  else if (over) line = "This session has ended. The recording is with your HR team.";
  else if (c.days > 0) line = `${plural(c.days, "day")}, ${plural(c.hours, "hour")} and ${plural(c.mins, "minute")} from now.`;
  else if (c.hours > 0) line = `${plural(c.hours, "hour")}, ${plural(c.mins, "minute")} and ${plural(c.secs, "second")} from now.`;
  else line = `${plural(c.mins, "minute")} and ${plural(c.secs, "second")} from now.`;
  return (
    <p style={{ ...serif, fontSize: 24, lineHeight: 1.25, fontWeight: 300, fontStyle: "italic", color: live ? RED : INK }}
      data-testid={live ? "countdown-live" : over ? "countdown-over" : "countdown"}>
      {line}
    </p>
  );
}

// "Add to calendar": a Google Calendar link and an .ics file made in the browser.
function calendarLinks(start, title, details, where) {
  const end = new Date(start.getTime() + MINUTES * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const g = new URL("https://calendar.google.com/calendar/render");
  g.searchParams.set("action", "TEMPLATE");
  g.searchParams.set("text", title);
  g.searchParams.set("dates", `${fmt(start)}/${fmt(end)}`);
  g.searchParams.set("details", details);
  g.searchParams.set("location", where);
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//LIFTR//Webinar//EN", "BEGIN:VEVENT",
    `UID:liftr-nutrition-${fmt(start)}@liftr.fit`, `DTSTAMP:${fmt(new Date())}`, `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
    `SUMMARY:${title}`, `DESCRIPTION:${details.replace(/\n/g, "\\n")}`, `LOCATION:${where}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  return { google: g.toString(), ics: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}` };
}

const Rule = () => <hr style={{ border: 0, borderTop: `1px solid ${RULE}`, margin: 0 }} />;

// A photo with a small-caps caption under it, like a print caption.
const Photo = ({ src, alt, caption, ratio = "3 / 2", className = "", position = "center" }) => (
  <figure className={className}>
    <img src={src} alt={alt} loading="lazy" className="block w-full" style={{ aspectRatio: ratio, objectFit: "cover", objectPosition: position }} />
    {caption && <figcaption className="mt-2" style={smallCaps}>{caption}</figcaption>}
  </figure>
);

// A numbered section: "03" and the title in a left column, the content on the right on a desktop.
function Sec({ n, title, children, id, photo, center }) {
  return (
    <section id={id} className={`py-10 lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16 lg:py-14 ${center ? "lg:items-center" : ""}`}>
      <div className="mb-6 lg:mb-0">
        <div style={{ ...smallCaps }}>{n}</div>
        <h2 className="mt-2 text-3xl lg:text-4xl" style={{ ...serif, fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.01em" }}>{title}</h2>
        {photo && <Photo {...photo} className={photo.className || "mt-6 max-w-sm lg:max-w-none"} />}
      </div>
      <div>{children}</div>
    </section>
  );
}

const Btn = ({ href, children, solid, download, testid, small }) => (
  <a href={href} download={download} target={download ? undefined : "_blank"} rel="noreferrer" data-testid={testid}
    className={small ? "inline-flex h-9 items-center justify-center whitespace-nowrap px-3.5 text-xs font-medium" : "inline-flex h-11 items-center justify-center px-5 text-sm"}
    style={solid ? { background: INK, color: PAPER } : { border: `1px solid ${INK}`, color: INK }}>
    {children}
  </a>
);

function Quiz() {
  const [picked, setPicked] = useState({});
  const answered = Object.keys(picked).length;
  const right = QUIZ.filter((x, i) => picked[i] === x.truth).length;
  return (
    <div data-testid="quiz">
      <Rule />
      {QUIZ.map((item, i) => {
        const p = picked[i];
        const done = p !== undefined;
        const ok = done && p === item.truth;
        return (
          <div key={item.q}>
            <div className="grid gap-3 py-4 sm:grid-cols-[1fr_auto] sm:items-start">
              <div>
                <div style={{ ...serif, fontSize: 19, fontWeight: 500, lineHeight: 1.3 }}>{item.q}</div>
                {done && (
                  <p className="mt-1.5 text-sm" style={{ color: MUTED }}>
                    <span style={{ color: ok ? INK : RED, fontWeight: 600 }}>{ok ? "Right." : item.truth ? "True." : "False."}</span> {item.why}
                  </p>
                )}
              </div>
              {!done && (
                <div className="flex gap-2">
                  {[["True", true], ["False", false]].map(([label, v]) => (
                    <button key={label} type="button" onClick={() => setPicked((s) => ({ ...s, [i]: v }))}
                      className="h-9 px-4 text-sm" style={{ border: `1px solid ${INK}`, color: INK, background: "transparent" }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Rule />
          </div>
        );
      })}
      {answered === QUIZ.length && (
        <p className="pt-4 text-sm" style={{ color: MUTED }} data-testid="quiz-score">
          {right} of {QUIZ.length}. {right === QUIZ.length ? "You could run this webinar. Come anyway, bring a colleague." : "The webinar covers every one of these, and plenty more."}
        </p>
      )}
    </div>
  );
}

function Faq() {
  return (
    <div>
      <Rule />
      {FAQ.map((f) => (
        <div key={f.q}>
          <details className="group py-4">
            <summary className="flex cursor-pointer list-none items-baseline justify-between gap-4" style={{ ...serif, fontSize: 19, fontWeight: 500 }}>
              <span>{f.q}</span>
              <span className="text-xl transition group-open:rotate-45" style={{ color: MUTED }} aria-hidden="true">+</span>
            </summary>
            <p className="mt-2 pr-8 text-[15px]" style={{ color: MUTED }}>{f.a}</p>
          </details>
          <Rule />
        </div>
      ))}
    </div>
  );
}

export default function NutritionWebinar() {
  const [params] = useSearchParams();
  const company = (params.get("for") || "").trim().slice(0, 60);
  const join = (params.get("join") || DEFAULT_JOIN).trim();
  const start = useMemo(() => {
    const d = new Date(params.get("at") || DEFAULT_START);
    return Number.isNaN(d.getTime()) ? new Date(DEFAULT_START) : d;
  }, [params]);

  useEffect(() => { document.title = "LIFTR | Nutrition webinar"; }, []);

  // The date in Dubai time, and in the viewer's own time when that's different.
  const dayName = new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: TZ }).format(start);
  const dayNum = new Intl.DateTimeFormat("en-GB", { day: "numeric", timeZone: TZ }).format(start);
  const monthName = new Intl.DateTimeFormat("en-GB", { month: "long", timeZone: TZ }).format(start);
  const time = new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ }).format(start).replace(/\s?(am|pm)/i, " $1");
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localTime = viewerTz && viewerTz !== TZ
    ? new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" }).format(start) : null;

  const title = `LIFTR nutrition webinar${company ? ` for ${company}` : ""}`;
  const cal = calendarLinks(start, title,
    `25 minutes on Zoom with Anish Menon, founder of LIFTR, on the food side of focus and energy at work: the basics, how to read a label, marketing gimmicks and myths, build your own meal plan.${join ? `\nJoin: ${join}` : "\nZoom link to follow from LIFTR."}`,
    join || "Zoom");
  const totalMin = AGENDA.reduce((n, a) => n + a.min, 0);

  return (
    <div className="min-h-screen" style={{ background: PAPER, color: INK, fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <link rel="stylesheet" href={FONTS} />
      <div className="mx-auto max-w-6xl px-5 pb-16 sm:px-8">
        {/* Masthead: sticky, so Join and Add to calendar stay in reach while scrolling (Anish, 30 Oct) */}
        <header className="sticky top-0 z-20 -mx-5 px-5 sm:-mx-8 sm:px-8" style={{ background: PAPER, borderBottom: `1px solid ${RULE}` }}>
          <div className="flex items-center justify-between gap-4 py-4">
            <img src="/brand/liftr-wordmark.png" alt="LIFTR" style={{ height: 26, width: "auto" }} className="sm:h-8" />
            <div className="flex items-center gap-4">
              <div style={smallCaps} className="hidden lg:block">{dayName} {dayNum} {monthName} · {time} Dubai time · Zoom</div>
              <div className="flex gap-2">
                {join
                  ? <Btn href={join} solid small testid="join-top">Join on Zoom</Btn>
                  : <Btn href={cal.google} solid small testid="add-google-top">Google Calendar</Btn>}
                <Btn href={cal.ics} download="liftr-nutrition-webinar.ics" small testid="add-ics-top">Add to calendar</Btn>
              </div>
            </div>
          </div>
        </header>

        {/* Invitation: the copy on the left, the date, countdown and buttons on the right */}
        <div className="grid gap-10 py-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16 lg:py-16">
          <div>
            <div style={smallCaps}>{company ? `${company} · ` : ""}A {MINUTES}-minute nutrition session</div>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl" style={{ ...serif, fontWeight: 300, lineHeight: 1.0, letterSpacing: "-0.02em" }}>
              Eat better<br />by <em style={{ fontStyle: "italic", fontWeight: 500 }}>lunch.</em>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed" style={{ color: INK }}>
              With Anish Menon, founder of LIFTR. Not a diet talk. The handful of food decisions that decide whether you&apos;re sharp at 3 pm
              or staring at the screen, how much you get done in a day, and what&apos;s left in the tank when you get home. Explained with
              the food you already eat, with three things you do live.
            </p>
          </div>

          <div className="lg:pt-2">
            <div className="flex items-end gap-4" data-testid="when">
              <div style={{ ...serif, fontSize: 96, lineHeight: 0.85, fontWeight: 300, letterSpacing: "-0.04em" }}>{dayNum}</div>
              <div className="pb-1">
                <div style={{ ...serif, fontSize: 28, lineHeight: 1, fontWeight: 500 }}>{monthName}</div>
                <div className="mt-1.5" style={{ ...smallCaps, color: INK }}>{dayName} · {time} Dubai time</div>
                {localTime && <div className="mt-1" style={{ ...smallCaps }}>{localTime} where you are</div>}
              </div>
            </div>
            <div className="mt-6">
              <Countdown start={start} />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {join
                ? <Btn href={join} solid testid="join">Join on Zoom</Btn>
                : <Btn href={cal.google} solid testid="add-google">Add to Google Calendar</Btn>}
              <Btn href={cal.ics} download="liftr-nutrition-webinar.ics" testid="add-ics">{join ? "Add to calendar" : "Outlook / Apple"}</Btn>
            </div>
            <p className="mt-3 text-sm" style={{ color: MUTED }}>
              {join ? "The Zoom link is in the calendar invite too." : "LIFTR hosts the Zoom. The link will appear here and go out through your HR team before the day. Put it in your calendar now."}
            </p>
          </div>
        </div>
        <Rule />

        <Sec n="01 · Why" title="Most of the 3 pm slump is lunch.">
          <p className="text-lg leading-relaxed">
            Brain fog after lunch. The third coffee. Emails that take twice as long at 4 pm as they did at 10. Nothing left for the gym,
            the kids or the course you meant to do after work. Most of that isn&apos;t willpower or workload. It&apos;s what you ate, when you
            ate it, and what you drank with it.
          </p>
          <p className="mt-4 text-lg leading-relaxed">
            The fix isn&apos;t a diet. It&apos;s understanding five or six things once, properly, and changing lunch. That&apos;s what this
            session is: the first lesson our members get, in {MINUTES} minutes, aimed at your working day.
          </p>
          <Photo src={IMG.smoothie} alt="A glass of smoothie held over a table of fresh fruit" className="mt-8" caption="“Healthy” tells you about vitamins, not calories. The desk smoothie is a big part of the 3 pm slump." />
          <dl className="mt-8 grid gap-6 sm:grid-cols-3">
            {[["Focus", "Longer stretches of real work after lunch, fewer slumps, less brain fog."],
              ["Energy", "The same tank at 6 pm as at 9 am, so the evening is yours: training, family, learning something."],
              ["Sleep", "When to stop the coffee and what to do about dinner, so tomorrow starts sharp."]].map(([h, t]) => (
              <div key={h} style={{ borderTop: `1px solid ${INK}` }} className="pt-3">
                <dt style={{ ...serif, fontSize: 22, fontWeight: 500 }}>{h}</dt>
                <dd className="mt-1 text-[15px]" style={{ color: MUTED }}>{t}</dd>
              </div>
            ))}
          </dl>
        </Sec>
        <Rule />

        <Sec n="02 · What we'll cover" title={`${totalMin} minutes, four things.`} photo={{ src: IMG.sugar, alt: "Sugar pouring out of a soft-drink can into a glass", ratio: "4 / 5", caption: "What’s in the can: about nine teaspoons of sugar in one regular soft drink." }}>
          <ol data-testid="agenda">
            {AGENDA.map((a, i) => (
              <li key={a.title} className="grid grid-cols-[3rem_1fr] gap-4 py-4 sm:grid-cols-[4rem_1fr]" style={{ borderTop: i ? `1px solid ${RULE}` : 0 }}>
                <div style={{ ...smallCaps, color: INK }} className="pt-1.5">{a.min} min</div>
                <div>
                  <div style={{ ...serif, fontSize: 22, fontWeight: 500, lineHeight: 1.2 }}>{a.title}</div>
                  <div className="mt-1 text-[15px]" style={{ color: MUTED }}>{a.text}</div>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-2 text-sm" style={{ color: MUTED }}>Then two minutes on how training and food work together.</p>
        </Sec>
        <Rule />

        <Sec n="03 · Not a lecture" title="Three things you'll do, live." photo={{ src: IMG.cola, alt: "A glass of cola with ice and lemon", caption: "Activity 1: rank the drinks by calories. The latte is not where you think." }}>
          <div className="grid gap-8 sm:grid-cols-3">
            {ACTIVITIES.map((a, i) => (
              <div key={a.title}>
                <div style={{ ...serif, fontSize: 44, fontWeight: 300, lineHeight: 1, color: RED }}>{i + 1}</div>
                <div className="mt-3" style={{ ...serif, fontSize: 20, fontWeight: 500, lineHeight: 1.2 }}>{a.title}</div>
                <div className="mt-1.5 text-[15px]" style={{ color: MUTED }}>{a.text}</div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm" style={{ color: MUTED }}>
            Keep a packet of anything with a nutrition label within reach: biscuits, chips, a protein bar, a juice carton. Camera off is fine.
          </p>
        </Sec>
        <Rule />

        <Sec n="04 · Before and after" title="What people think today, and what they leave knowing." photo={{ src: IMG.plate, alt: "A home-cooked plate: roast chicken, broccoli, rice and lentils", ratio: "4 / 5", caption: "The plate you’ll build in block four: half veg, a palm of protein, a handful of carbs, a thumb of fat." }}>
          <div data-testid="shifts">
            <div className="hidden grid-cols-2 gap-8 pb-2 sm:grid" style={smallCaps}><span>They say</span><span>Actually</span></div>
            {SHIFTS.map((s) => (
              <div key={s.before} className="grid gap-2 py-4 sm:grid-cols-2 sm:gap-8" style={{ borderTop: `1px solid ${RULE}` }}>
                <div style={{ ...serif, fontSize: 18, fontStyle: "italic", fontWeight: 300, color: MUTED }}>&ldquo;{s.before}&rdquo;</div>
                <div className="text-[15px]">{s.after}</div>
              </div>
            ))}
          </div>
        </Sec>
        <Rule />

        <Sec n="05 · Try it now" title="How many of these do you believe?">
          <Quiz />
        </Sec>
        <Rule />

        <Sec n="06 · From LIFTR Dubai members" title="More energy in the day, not just a number on the scale." photo={{ src: IMG.group, alt: "A LIFTR batch after a morning session", caption: "A LIFTR batch after a morning session", position: "center 40%" }}>
          <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2" data-testid="reviews">
            {REVIEWS.map((r) => (
              <figure key={r.who}>
                <blockquote style={{ ...serif, fontSize: 18, lineHeight: 1.4, fontWeight: 300 }}>
                  <span style={{ color: RED, fontSize: 28, lineHeight: 0, verticalAlign: "-0.3em", marginRight: 2 }}>&ldquo;</span>{r.text}&rdquo;
                </blockquote>
                <figcaption className="mt-2" style={smallCaps}>{r.who} · Google review</figcaption>
              </figure>
            ))}
          </div>
          <p className="mt-8 text-sm" style={{ color: MUTED }}>
            <a href={MAPS} target="_blank" rel="noreferrer" className="underline">All 70 reviews of LIFTR Dubai on Google</a>, rated 5.0.
          </p>
          <blockquote className="mt-8" style={{ ...serif, fontSize: 22, lineHeight: 1.35, fontWeight: 300, borderLeft: `2px solid ${RED}`, paddingLeft: 20 }}>
            The part that doesn&apos;t show up in a review is what members tell us at the gym: the promotion that came after a year of
            having energy left at 5 pm, the one who finally applied for the bigger company, the ones who started studying again in the
            evenings. That&apos;s the result we&apos;re after. The scale is a side effect.
          </blockquote>
          <p className="mt-2" style={smallCaps}>Anish Menon, founder</p>
        </Sec>
        <Rule />

        {/* The host: the same pattern as every section, label and a small portrait on the left, the text on the right */}
        <Sec n="07 · Your host" center photo={{ src: IMG.anish, alt: "Anish Menon, founder of LIFTR", ratio: "4 / 5", position: "top", caption: "Anish Menon · Founder, LIFTR", className: "mt-4 w-48 sm:w-56" }}>
          <p className="text-lg leading-relaxed">
            Anish started LIFTR and runs this session himself. LIFTR runs eight coaching gyms: Dubai (Al Quoz), Bangalore, Hyderabad and Colombo. Every member
            gets a coach who looks at their meals, not just their workouts, and the same nutrition course this webinar is built from. Most of
            our members are beginners in their 30s and 40s with a desk job, so this is the conversation we have every day.
          </p>
        </Sec>
        <Rule />

        <Sec n="08 · Questions" title="Before you join.">
          <Faq />
          <p className="mt-6 text-[15px]" style={{ color: MUTED }}>
            Anything else, or a question you&apos;d like covered on the day: email Anish at{" "}
            <a href={`mailto:${EMAIL}?subject=Nutrition%20webinar`} className="underline" style={{ color: INK }} data-testid="email">{EMAIL}</a>.
          </p>
        </Sec>
        <Rule />

        {/* The training side: a clear push to LIFTR Dubai (Anish, 30 Oct: "okay to push a little more") */}
        <section id="train" className="grid gap-8 py-12 lg:grid-cols-2 lg:items-center lg:gap-16 lg:py-16">
          <Photo src={IMG.squat} alt="A LIFTR coach spotting a member’s squat" ratio="4 / 3" caption="Coached, not left alone: every session at LIFTR" />
          <div>
            <div style={smallCaps}>The other half</div>
            <h2 className="mt-3 text-3xl lg:text-4xl" style={{ ...serif, fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.01em" }}>
              Food gets you through the workday. Training gives you the evening.
            </h2>
            <p className="mt-4 text-lg leading-relaxed">
              Two or three strength sessions a week are what make the energy last past 6 pm: better sleep, the posture a desk takes away, and
              something left for the gym, the kids or the course you keep meaning to do.
            </p>
            <p className="mt-3 text-[15px]" style={{ color: MUTED }}>
              LIFTR Dubai is in Al Quoz. Coached sessions at 6, 7 and 8 in the morning and evening, a coach who checks your meals in the app,
              and the full version of today&apos;s nutrition course. Come and see it.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Btn href={DUBAI} solid testid="cta">See LIFTR Dubai</Btn>
            </div>
            <p className="mt-3 text-sm" style={{ color: MUTED }}>Tell them you came from the webinar.</p>
          </div>
        </section>
        <Rule />

        <footer className="flex flex-wrap items-center justify-between gap-2 py-5" style={smallCaps}>
          <span>LIFTR Strength &amp; Conditioning · <span style={{ letterSpacing: 0, textTransform: "none" }}>{CREDITS}</span></span>
          <span>
            <a href={DUBAI} target="_blank" rel="noreferrer" className="underline">liftr.fit</a> · <a href={`mailto:${EMAIL}`} className="underline">{EMAIL}</a>
          </span>
        </footer>
      </div>
    </div>
  );
}
